import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n";
import { useMarket } from "@/market";
import { PRIVACY, AUTHORITIES, processors, retention } from "@/content/privacy";

const Section = ({ title, children, testId }) => (
  <div className="mt-10" data-testid={testId}>
    <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2645]">{title}</h2>
    <div className="mt-4 text-sm sm:text-base leading-relaxed text-[#5C6A87] space-y-3">{children}</div>
  </div>
);

const Table = ({ cols, rows, highlight }) => (
  <div className="overflow-x-auto rounded-xl border border-[#E2E7F7] bg-white">
    <table className="w-full text-left text-sm">
      <thead className="bg-[#F6F8FD] text-xs uppercase tracking-wide text-[#5C6A87]">
        <tr>{cols.map((c) => <th key={c} className="px-4 py-3 font-semibold">{c}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-[#E2E7F7]">
        {rows.map((r, i) => (
          <tr key={i} className={highlight === i ? "bg-[#EEF2FF]" : ""}>
            {r.map((cell, j) => <td key={j} className={`px-4 py-3 align-top ${j === 0 ? "font-medium text-[#0F2645]" : "text-[#5C6A87]"}`}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PrivacyPage() {
  const { t, lang } = useLanguage();
  const { country } = useMarket();
  const c = PRIVACY[lang] || PRIVACY.fr;
  const L = c.labels;
  const l = PRIVACY[lang] ? lang : "fr";

  useEffect(() => { document.title = `${c.title} | Devisto`; }, [c.title]);

  const rightsRows = AUTHORITIES.map((a) => [
    `${a.name[l]}${a.code === country ? ` (${L.yourCountry})` : ""}`,
    <a key={a.code} href={a.url} target="_blank" rel="noreferrer" className="text-[#3A5CC8] underline-offset-2 hover:underline" data-testid={`privacy-authority-${a.code}`}>{a.authority[l]}</a>,
    a.law[l],
  ]);
  const highlight = AUTHORITIES.findIndex((a) => a.code === country);

  return (
    <div data-testid="privacy-page" className="font-body bg-[#F6F8FD] text-[#0F2645] min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 sm:py-24">
          <Reveal>
            <p className="text-sm font-bold tracking-[0.1em] uppercase text-[#3A5CC8]">{t("Informations légales")}</p>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F2645]">{c.title}</h1>
            <p className="mt-3 text-xs text-[#5C6A87]">{c.updated}</p>
            <p data-testid="privacy-controller" className="mt-6 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 text-sm text-[#5C6A87]"><span className="font-semibold text-[#0F2645]">{L.controller} :</span> {c.controller}</p>
          </Reveal>
          {c.sections.slice(0, 3).map((s) => (
            <Section key={s.title} title={s.title}>{s.body.map((p, i) => <p key={i}>{p}</p>)}</Section>
          ))}
          <Section title={L.processors} testId="privacy-processors"><Table cols={L.cols} rows={processors(l)} /></Section>
          {c.sections.slice(3, 6).map((s) => (
            <Section key={s.title} title={s.title}>{s.body.map((p, i) => <p key={i}>{p}</p>)}</Section>
          ))}
          <Section title={L.retention} testId="privacy-retention"><Table cols={L.retCols} rows={retention(l)} /></Section>
          {c.sections.slice(6, 7).map((s) => (
            <Section key={s.title} title={s.title}>{s.body.map((p, i) => <p key={i}>{p}</p>)}</Section>
          ))}
          <Section title={L.rights} testId="privacy-rights"><Table cols={L.rightsCols} rows={rightsRows} highlight={highlight} /></Section>
          {c.sections.slice(7).map((s) => (
            <Section key={s.title} title={s.title}>{s.body.map((p, i) => <p key={i}>{p}</p>)}</Section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
